import * as Dialog from '@radix-ui/react-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { Button } from '@/components/ui/button';
import { FormError, Label } from '@/components/ui/field';
import { ApiError, api } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import type { JobDetail } from '@shared/schemas/job';

/*
 * The office's photo path — ROADMAP.md §4.6.
 *
 * The crew's own capture flow arrives with the employee portal (6.4); until
 * then, and after it for the crew-texted-it-in case, the office attaches the
 * photo and names who took it. The photographer has to be on the crew,
 * because `uploaded_by_employee_id` is whose evidence it is — the server
 * refuses anybody else.
 *
 * Uploading against a photo-required task IS completing it (§3.1) — the
 * dialog says so under the task picker.
 */

const SELECT_CLASSES =
  'min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body';

export function PhotoUploadDialog({
  job,
  open,
  onOpenChange,
}: {
  job: JobDetail;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [jobTaskId, setJobTaskId] = useState('');
  const [uploadedBy, setUploadedBy] = useState(job.assignees[0]?.employeeId ?? '');
  const [kind, setKind] = useState<'before' | 'after' | 'issue'>('after');
  const [caption, setCaption] = useState('');

  const mutation = useMutation({
    mutationFn: (form: FormData) => api.upload<JobDetail>(`/api/admin/jobs/${job.id}/photos`, form),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'jobs'] }),
  });
  const error = mutation.error instanceof ApiError ? mutation.error : null;

  const chosenTask = job.tasks.find((task) => task.id === jobTaskId);
  const completesTask =
    chosenTask !== undefined && chosenTask.requiresPhoto && !chosenTask.isCompleted;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!file || uploadedBy === '') return;

    const form = new FormData();
    form.set('photo', file);
    if (jobTaskId !== '') form.set('jobTaskId', jobTaskId);
    form.set('uploadedByEmployeeId', uploadedBy);
    form.set('kind', kind);
    if (caption.trim() !== '') form.set('caption', caption.trim());

    mutation.mutate(form, {
      onSuccess: () => {
        toast.success(completesTask ? 'Photo saved — task completed.' : 'Photo saved.');
        onOpenChange(false);
      },
    });
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <Dialog.Title className="text-h2 text-ink-900">Add a photo</Dialog.Title>
          <Dialog.Description className="mt-1 text-body text-ink-500">
            GPS and other camera data are stripped on the way in.
          </Dialog.Description>

          {job.assignees.length === 0 ? (
            <p className="bg-paper-50 mt-5 rounded-sm border border-ink-300 px-3 py-2.5 text-body text-ink-700">
              Photos need a crew first — they are the crew’s evidence. Assign someone, then add the
              photo in their name.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-5 space-y-4">
              {error ? <FormError>{error.message}</FormError> : null}

              <div className="space-y-1.5">
                <Label htmlFor="photo-file" required>
                  Photo
                </Label>
                <input
                  id="photo-file"
                  name="photo"
                  type="file"
                  required
                  accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null);
                  }}
                  className="block w-full text-body text-ink-700 file:mr-3 file:min-h-touch file:cursor-pointer file:rounded-md file:border-0 file:bg-honey-400 file:px-4 file:text-label file:font-medium file:text-ink-900 hover:file:bg-honey-300"
                />
                <p className="text-meta text-ink-500">JPEG, PNG, WebP or HEIC, up to 10MB.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="photo-task">For task</Label>
                <select
                  id="photo-task"
                  name="jobTaskId"
                  value={jobTaskId}
                  onChange={(event) => {
                    setJobTaskId(event.target.value);
                  }}
                  className={SELECT_CLASSES}
                >
                  <option value="">The job in general</option>
                  {job.tasks.map((task) => (
                    <option key={task.id} value={task.id}>
                      {task.name}
                    </option>
                  ))}
                </select>
                {completesTask ? (
                  <p className="text-meta text-ink-700">
                    This task is photo-required — saving this photo completes it.
                  </p>
                ) : null}
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label htmlFor="photo-by" required>
                    Taken by
                  </Label>
                  <select
                    id="photo-by"
                    name="uploadedByEmployeeId"
                    required
                    value={uploadedBy}
                    onChange={(event) => {
                      setUploadedBy(event.target.value);
                    }}
                    className={SELECT_CLASSES}
                  >
                    {job.assignees.map((entry) => (
                      <option key={entry.employeeId} value={entry.employeeId}>
                        {entry.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="photo-kind">Kind</Label>
                  <select
                    id="photo-kind"
                    name="kind"
                    value={kind}
                    onChange={(event) => {
                      setKind(event.target.value as 'before' | 'after' | 'issue');
                    }}
                    className={SELECT_CLASSES}
                  >
                    <option value="after">After</option>
                    <option value="before">Before</option>
                    <option value="issue">Issue</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="photo-caption">Caption</Label>
                <input
                  id="photo-caption"
                  name="caption"
                  type="text"
                  maxLength={512}
                  value={caption}
                  onChange={(event) => {
                    setCaption(event.target.value);
                  }}
                  className="min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Dialog.Close asChild>
                  <Button type="button" variant="outline">
                    Cancel
                  </Button>
                </Dialog.Close>
                <Button type="submit" disabled={mutation.isPending || file === null}>
                  {mutation.isPending ? 'Uploading…' : 'Save photo'}
                </Button>
              </div>
            </form>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
